// Live state from /api/live: devices, pending approvals, task progress,
// device events and UI-tree subscription pushes. One socket for the app,
// reconnecting with backoff.

import type {
  ApprovalInfo,
  DeviceEventInfo,
  DeviceSummary,
  LiveEvent,
  ProtoJson,
  TaskInfo,
  TaskStepInfo,
} from "@fistos/shared/api";
import { createStore, produce, reconcile } from "solid-js/store";

export interface PlannerState {
  step: number;
  state: "thinking" | "decided" | "error";
  detail: string;
  at: number;
}

interface LiveStore {
  connected: boolean;
  devices: Record<string, DeviceSummary>;
  approvals: Record<string, ApprovalInfo>;
  tasks: Record<string, TaskInfo>;
  steps: Record<string, TaskStepInfo[]>;
  planner: Record<string, PlannerState>;
  events: Record<string, DeviceEventInfo[]>;
  uiTree: Record<string, ProtoJson>;
}

const [live, setLive] = createStore<LiveStore>({
  connected: false,
  devices: {},
  approvals: {},
  tasks: {},
  steps: {},
  planner: {},
  events: {},
  uiTree: {},
});

export { live };

const EVENT_WINDOW = 200;

function apply(e: LiveEvent): void {
  switch (e.type) {
    case "snapshot":
      setLive("devices", reconcile(Object.fromEntries(e.devices.map((d) => [d.id, d]))));
      setLive("approvals", reconcile(Object.fromEntries(e.approvals.map((a) => [a.id, a]))));
      return;
    case "device":
      setLive("devices", e.device.id, reconcile(e.device));
      return;
    case "device_removed":
      setLive(
        "devices",
        produce((d) => delete d[e.deviceId]),
      );
      return;
    case "approval":
      setLive("approvals", e.approval.id, reconcile(e.approval));
      return;
    case "task":
      setLive("tasks", e.task.id, reconcile(e.task));
      return;
    case "task_step":
      setLive(
        "steps",
        produce((s) => {
          const list = (s[e.taskId] ??= []);
          const i = list.findIndex((x) => x.step === e.step.step);
          if (i >= 0) list[i] = e.step;
          else list.push(e.step);
          list.sort((a, b) => a.step - b.step);
        }),
      );
      return;
    case "planner":
      setLive("planner", e.taskId, { step: e.step, state: e.state, detail: e.detail, at: Date.now() });
      return;
    case "event":
      setLive(
        "events",
        produce((s) => {
          const list = (s[e.deviceId] ??= []);
          list.push(e.event);
          if (list.length > EVENT_WINDOW) list.splice(0, list.length - EVENT_WINDOW);
        }),
      );
      return;
    case "ui_tree":
      setLive("uiTree", e.deviceId, reconcile(e.snapshot));
      return;
  }
}

/** Seeds task steps fetched over HTTP (live events only carry new ones). */
export function seedSteps(taskId: string, steps: TaskStepInfo[]): void {
  setLive("steps", taskId, reconcile(steps));
}

export function seedTasks(tasks: TaskInfo[]): void {
  for (const t of tasks) setLive("tasks", t.id, reconcile(t));
}

export function seedEvents(deviceId: string, events: DeviceEventInfo[]): void {
  setLive("events", deviceId, reconcile(events));
}

export function setUiTree(deviceId: string, tree: ProtoJson): void {
  setLive("uiTree", deviceId, reconcile(tree));
}

let socket: WebSocket | null = null;
let retry = 0;
let stopped = false;

export function connectLive(): void {
  stopped = false;
  if (socket) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/api/live`);
  socket = ws;
  ws.onopen = () => {
    retry = 0;
    setLive("connected", true);
  };
  ws.onmessage = (m) => {
    try {
      apply(JSON.parse(m.data as string) as LiveEvent);
    } catch (err) {
      console.error("bad live event", err);
    }
  };
  ws.onclose = (ev) => {
    socket = null;
    setLive("connected", false);
    if (stopped || ev.code === 4001) return;
    const delay = Math.min(30_000, 500 * 2 ** retry++);
    setTimeout(connectLive, delay);
  };
}

export function disconnectLive(): void {
  stopped = true;
  socket?.close();
  socket = null;
}
